// src/pages/CreditsPage.jsx
// Enterprise Credit System — Role-aware Credits Dashboard
// Admin (r1/r2): All-tenants overview table
// Tenant (r4/r7): Their own wallet + usage + transactions
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/useAuth';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter';
import {
  getMyWallet, getMyUsage, getMyTransactions, adminGetAllWallets, adminTopUpWallet,
  getUsageBreakdown, exportUsageCSV,
  getPartnerCredits, partnerTopUpPool, partnerAllocateCredits, partnerRevokeCredits,
  adminGetPartners
} from '../api/credits';
import { PageHeader, LoadingSpinner } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';

// ── Helpers ────────────────────────────────────────────────
const fmt = (n, dec = 4) => (Number(n) || 0).toFixed(dec);
// const fmtDate = (d) => d ? new Date(d).toLocaleString() : '—'; // REPLACED BY HOOK

const MODULE_COLORS = {
  DATA: 'bg-indigo-100 text-indigo-700',
  ML: 'bg-amber-100 text-amber-700',
  GENAI: 'bg-emerald-100 text-emerald-700',
  WORKFLOW: 'bg-blue-100 text-blue-700',
};
const MODULE_BAR_COLORS = {
  DATA:     'bg-indigo-500',
  ML:       'bg-amber-500',
  GENAI:    'bg-emerald-500',
  WORKFLOW: 'bg-blue-500',
};
const MODULE_ICONS = { DATA: '🔷', ML: '🟡', GENAI: '🟣', WORKFLOW: '🟢' };
const STATUS_COLORS = {
  completed: 'bg-green-100 text-green-700',
  running: 'bg-blue-100 text-blue-700',
  failed: 'bg-red-100 text-red-700',
};

// ── Shared Components ──────────────────────────────────────
function StatCard({ label, value, sub, icon, borderColor = 'border-indigo-500' }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 border-l-4 ${borderColor} p-5 flex-1 min-w-[160px]`}>
      <div className="flex items-center gap-2 mb-2">
        {icon && <DynamicIcon name={icon} className="w-4 h-4 text-gray-400" />}
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

function CreditBar({ used, total, reserved }) {
  const pUsed = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  const pRes  = total > 0 ? Math.min(100, (reserved / total) * 100) : 0;
  const color = pUsed >= 90 ? 'bg-red-500' : pUsed >= 80 ? 'bg-amber-400' : 'bg-indigo-500';
  return (
    <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex w-full">
      <div className={`${color} transition-all`} style={{ width: `${pUsed}%` }} />
      <div className="bg-amber-300 opacity-60 transition-all" style={{ width: `${pRes}%` }} />
    </div>
  );
}


// ── Top-Up Modal ──────────────────────────────────────────
function TopUpModal({ tenant, token, onClose, onSuccess }) {
  const [amount, setAmount]   = useState('');
  const [note, setNote]       = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setError('Amount must be greater than 0'); return; }
    setLoading(true); setError(null);
    try {
      await adminTopUpWallet(token, tenant.tenant_id, amt, note);
      onSuccess(amt, tenant.tenant_name || tenant.tenant_id);
    } catch (err) {
      setError(err.message || 'Top-up failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
              <DynamicIcon name="CreditCard" className="w-4 h-4 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">Manual Top-Up</h3>
              <p className="text-xs text-gray-400 truncate max-w-[220px]">{tenant.tenant_name || tenant.tenant_id}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <DynamicIcon name="X" className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Current balance info strip */}
          <div className="bg-gray-50 rounded-xl px-4 py-3 flex justify-between text-sm">
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Current Balance</p>
              <p className="font-bold text-gray-900">{fmt(tenant.balance, 2)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400 mb-0.5">Monthly Budget</p>
              <p className="font-semibold text-gray-700">{Number(tenant.monthly_allocation) > 0 ? fmt(tenant.monthly_allocation, 0) : '—'}</p>
            </div>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Top-Up Amount <span className="text-red-400">*</span></label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-semibold">₢</span>
              <input
                id="topup-amount"
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="e.g. 500"
                required
                className="w-full pl-7 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
              />
            </div>
          </div>

          {/* Note */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Note <span className="text-gray-300">(optional)</span></label>
            <input
              id="topup-note"
              type="text"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="e.g. Monthly allocation top-up"
              maxLength={200}
              className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2.5">
              ⚠️ {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading || !amount}
              className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors flex items-center justify-center gap-2">
              {loading ? (
                <><DynamicIcon name="Loader2" className="w-4 h-4 animate-spin" /> Processing…</>
              ) : (
                <><DynamicIcon name="PlusCircle" className="w-4 h-4" /> Add Credits</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ── Pool Top-Up Modal (Platform Admin → Partner Pool) ─────
function PoolTopUpModal({ partner, token, onClose, onSuccess }) {
  const [amount, setAmount]   = useState('');
  const [note, setNote]       = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setError('Amount must be greater than 0'); return; }
    setLoading(true); setError(null);
    try {
      await partnerTopUpPool(token, partner.id, amt, note);
      onSuccess(amt, partner.name);
    } catch (err) {
      setError(err.message || 'Top-up failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
              <DynamicIcon name="Zap" className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">Top Up Partner Pool</h3>
              <p className="text-xs text-gray-400 truncate max-w-[220px]">{partner.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <DynamicIcon name="X" className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div className="bg-purple-50 rounded-xl px-4 py-3 text-sm">
            <p className="text-xs text-purple-500 mb-0.5">Partner</p>
            <p className="font-bold text-purple-800">{partner.name}</p>
            <p className="text-xs text-purple-400 mt-1">{partner.email}</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Credits to Add <span className="text-red-400">*</span></label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-semibold">₢</span>
              <input
                type="number" min="0.01" step="0.01" value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="e.g. 10000" required
                className="w-full pl-7 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Note <span className="text-gray-300">(optional)</span></label>
            <input
              type="text" value={note} onChange={e => setNote(e.target.value)}
              placeholder="e.g. Monthly allocation"
              maxLength={200}
              className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400"
            />
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2.5">⚠️ {error}</div>}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading || !amount}
              className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors flex items-center justify-center gap-2">
              {loading ? <><DynamicIcon name="Loader2" className="w-4 h-4 animate-spin" /> Processing…</> : <><DynamicIcon name="Zap" className="w-4 h-4" /> Add to Pool</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ── Partner Pools Tab (Platform Admin) ────────────────────
function PartnerPoolsTab({ token }) {
  const [partners, setPartners]         = useState([]);
  const [pools, setPools]               = useState({});   // { partnerId: summaryObj }
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [topUpPartner, setTopUpPartner] = useState(null);
  const [successMsg, setSuccessMsg]     = useState(null);
  const [expanded, setExpanded]         = useState({});   // { partnerId: bool }

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const partnerList = await adminGetPartners(token);
      const list = Array.isArray(partnerList) ? partnerList : [];
      setPartners(list);
      const summaries = await Promise.all(
        list.map(p => getPartnerCredits(token, p.id).catch(() => ({
          partner_id: p.id, pool_total: 0, pool_allocated: 0, pool_available: 0, tenant_count: 0, tenants: []
        })))
      );
      const poolMap = {};
      summaries.forEach(s => { poolMap[s.partner_id] = s; });
      setPools(poolMap);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const totalPool      = Object.values(pools).reduce((s, p) => s + Number(p.pool_total     || 0), 0);
  const totalAllocated = Object.values(pools).reduce((s, p) => s + Number(p.pool_allocated || 0), 0);
  const totalAvailable = Object.values(pools).reduce((s, p) => s + Number(p.pool_available || 0), 0);

  return (
    <>
      {/* Summary Cards */}
      <div className="flex flex-wrap gap-4 mb-6">
        <StatCard label="Total Partners" value={partners.length} sub="Registered partners" icon="Users" borderColor="border-purple-500" />
        <StatCard label="Total Pool" value={fmt(totalPool, 2)} sub="Credited by platform" icon="Zap" borderColor="border-indigo-500" />
        <StatCard label="Allocated" value={fmt(totalAllocated, 2)} sub="Distributed to tenants" icon="ArrowRightLeft" borderColor="border-amber-400" />
        <StatCard label="Available" value={fmt(totalAvailable, 2)} sub="Ready to distribute" icon="CheckCircle" borderColor="border-green-500" />
      </div>

      {successMsg && (
        <div className="mb-5 flex items-center justify-between bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm">
          <span>✅ {successMsg}</span>
          <button onClick={() => setSuccessMsg(null)} className="ml-4 text-green-400 hover:text-green-600">✕</button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="font-semibold text-gray-800 text-sm">Partner Credit Pools</h3>
            <p className="text-xs text-gray-400 mt-0.5">Click a partner row to see per-tenant allocation</p>
          </div>
          <button onClick={load} disabled={loading}
            className="flex items-center gap-1.5 text-xs font-medium text-purple-600 hover:text-purple-800 disabled:opacity-50">
            <DynamicIcon name="RefreshCw" className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {error && <div className="mx-5 my-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2.5 text-sm">⚠️ {error}</div>}

        {loading ? (
          <div className="flex justify-center py-12"><LoadingSpinner /></div>
        ) : partners.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <DynamicIcon name="Inbox" className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p>No partners registered yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['', 'Partner', 'Total Pool', 'Allocated', 'Available', 'Tenants', 'Pool Usage', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {partners.map((p) => {
                  const pool    = pools[p.id] || { pool_total: 0, pool_allocated: 0, pool_available: 0, tenant_count: 0, tenants: [] };
                  const usePct  = pool.pool_total > 0 ? Math.min(100, (pool.pool_allocated / pool.pool_total) * 100) : 0;
                  const barColor = usePct >= 90 ? 'bg-red-500' : usePct >= 70 ? 'bg-amber-400' : 'bg-purple-500';
                  const isOpen  = !!expanded[p.id];
                  const tenants = pool.tenants || [];

                  return (
                    <React.Fragment key={p.id}>
                      {/* ── Partner Row ── */}
                      <tr
                        onClick={() => toggleExpand(p.id)}
                        className="hover:bg-purple-50 transition-colors cursor-pointer border-b border-gray-50"
                      >
                        {/* Expand chevron */}
                        <td className="px-3 py-3 w-8">
                          <DynamicIcon
                            name={isOpen ? 'ChevronDown' : 'ChevronRight'}
                            className="w-4 h-4 text-gray-400"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">{p.name}</div>
                          <div className="text-xs text-gray-400">{p.email}</div>
                        </td>
                        <td className="px-4 py-3 font-semibold text-gray-900">{fmt(pool.pool_total, 2)}</td>
                        <td className="px-4 py-3 text-amber-600 font-medium">{fmt(pool.pool_allocated, 2)}</td>
                        <td className="px-4 py-3 text-green-600 font-medium">{fmt(pool.pool_available, 2)}</td>
                        <td className="px-4 py-3 text-gray-600">{pool.tenant_count ?? 0}</td>
                        <td className="px-4 py-3 min-w-[140px]">
                          {pool.pool_total > 0 ? (
                            <div className="space-y-1">
                              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div className={`${barColor} h-full transition-all`} style={{ width: `${usePct}%` }} />
                              </div>
                              <span className={`text-xs font-semibold ${usePct >= 90 ? 'text-red-600' : 'text-gray-500'}`}>{usePct.toFixed(1)}%</span>
                            </div>
                          ) : <span className="text-gray-300 text-xs">No pool yet</span>}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={(e) => { e.stopPropagation(); setTopUpPartner(p); }}
                            className="flex items-center gap-1 text-xs font-semibold text-purple-600 hover:text-white border border-purple-200 hover:bg-purple-600 px-2.5 py-1.5 rounded-lg transition-all whitespace-nowrap">
                            <DynamicIcon name="Zap" className="w-3.5 h-3.5" /> Top Up Pool
                          </button>
                        </td>
                      </tr>

                      {/* ── Tenant Breakdown (expanded) ── */}
                      {isOpen && (
                        <tr className="border-b border-gray-100">
                          <td colSpan={8} className="px-0 py-0 bg-gray-50">
                            {tenants.length === 0 ? (
                              <div className="px-12 py-4 text-xs text-gray-400 italic">No tenants under this partner.</div>
                            ) : (
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b border-gray-100">
                                    <th className="pl-14 pr-4 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Tenant</th>
                                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Plan</th>
                                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Wallet Balance</th>
                                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Available</th>
                                    <th className="px-4 py-2 text-left font-semibold text-purple-500 uppercase tracking-wide">Partner Allocated</th>
                                    <th className="px-4 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Monthly Budget</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {tenants.map((t, ti) => (
                                    <tr key={t.tenant_id || ti} className="border-b border-gray-100 last:border-0 hover:bg-white transition-colors">
                                      <td className="pl-14 pr-4 py-2.5">
                                        <div className="font-medium text-gray-800">{t.tenant_name}</div>
                                        <div className="text-gray-400 font-mono text-xs">{t.tenant_id}</div>
                                      </td>
                                      <td className="px-4 py-2.5 text-gray-500">{t.plan_id || '—'}</td>
                                      <td className="px-4 py-2.5 font-semibold text-gray-800">{fmt(t.current_balance, 2)}</td>
                                      <td className="px-4 py-2.5">
                                        <span className={`font-semibold ${t.available_credits < 100 ? 'text-red-500' : 'text-green-600'}`}>
                                          {fmt(t.available_credits, 2)}
                                        </span>
                                      </td>
                                      <td className="px-4 py-2.5">
                                        <span className={`font-bold ${t.net_allocated_by_partner > 0 ? 'text-purple-600' : 'text-gray-300'}`}>
                                          {t.net_allocated_by_partner > 0 ? `+${fmt(t.net_allocated_by_partner, 2)}` : '0.00'}
                                        </span>
                                      </td>
                                      <td className="px-4 py-2.5 text-gray-500">{fmt(t.monthly_allocation, 0)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {topUpPartner && (
        <PoolTopUpModal
          partner={topUpPartner}
          token={token}
          onClose={() => setTopUpPartner(null)}
          onSuccess={(amt, name) => {
            setTopUpPartner(null);
            setSuccessMsg(`Successfully added ${fmt(amt, 2)} credits to ${name}'s pool.`);
            load();
          }}
        />
      )}
    </>
  );
}


// ════════════════════════════════════════════════════════════
// ADMIN VIEW — All Tenants Overview
// ════════════════════════════════════════════════════════════
function AdminCreditsView({ token }) {
  const { formatDate: fmtDate } = useTimezoneFormatter();
  const [tab, setTab]               = useState('wallets'); // 'wallets' | 'pools'
  const [wallets, setWallets]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [search, setSearch]         = useState('');
  const [topUpTenant, setTopUpTenant] = useState(null);   // tenant being topped up
  const [successMsg, setSuccessMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminGetAllWallets(token);
      setWallets(Array.isArray(data) ? data : data?.wallets || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = wallets.filter(w =>
    !search || (w.tenant_id || '').toLowerCase().includes(search.toLowerCase()) ||
    (w.tenant_name || '').toLowerCase().includes(search.toLowerCase())
  );

  // Summary stats
  const totalBalance  = wallets.reduce((s, w) => s + Number(w.balance || 0), 0);
  const totalReserved = wallets.reduce((s, w) => s + Number(w.reserved || w.reserved_credits || 0), 0);
  const criticalCount = wallets.filter(w => {
    const alloc = Number(w.monthly_allocation || 0);
    const avail = Number(w.available || w.balance || 0);
    return alloc > 0 && (alloc - avail) / alloc * 100 >= 80;
  }).length;

  return (
    <>
      {/* Tab Bar */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => setTab('wallets')}
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
            tab === 'wallets' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}>
          <span className="flex items-center gap-1.5">
            <DynamicIcon name="Wallet" className="w-4 h-4" /> Tenant Wallets
          </span>
        </button>
        <button
          onClick={() => setTab('pools')}
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
            tab === 'pools' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}>
          <span className="flex items-center gap-1.5">
            <DynamicIcon name="Zap" className="w-4 h-4" /> Partner Pools
          </span>
        </button>
      </div>

      {/* Partner Pools Tab */}
      {tab === 'pools' && <PartnerPoolsTab token={token} />}

      {/* Tenant Wallets Tab */}
      {tab === 'wallets' && <>
      {/* Summary Cards */}
      <div className="flex flex-wrap gap-4 mb-6">
        <StatCard label="Total Tenants" value={wallets.length} sub="With credit wallets" icon="Users" borderColor="border-indigo-500" />
        <StatCard label="Total Balance" value={fmt(totalBalance, 2)} sub="Across all tenants" icon="CreditCard" borderColor="border-green-500" />
        <StatCard label="Total Reserved" value={fmt(totalReserved, 2)} sub="Held by running jobs" icon="Clock" borderColor="border-amber-400" />
        <StatCard label="⚠️ Near Limit" value={criticalCount} sub="≥80% usage tenants" icon="AlertTriangle" borderColor="border-red-500" />
      </div>

      {/* Success Banner */}
      {successMsg && (
        <div className="mb-5 flex items-center justify-between bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm">
          <span>✅ {successMsg}</span>
          <button onClick={() => setSuccessMsg(null)} className="ml-4 text-green-400 hover:text-green-600">✕</button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800 text-sm">All Tenant Wallets</h3>
          <div className="flex items-center gap-3">
            <div className="relative">
              <DynamicIcon name="Search" className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search tenant…"
                className="pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 w-48"
              />
            </div>
            <button onClick={load} disabled={loading}
              className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50">
              <DynamicIcon name="RefreshCw" className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="mx-5 my-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2.5 text-sm">
            ⚠️ {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><LoadingSpinner /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <DynamicIcon name="Inbox" className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p>No wallets found. Top-up a tenant wallet to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-50 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Tenant', 'Balance', 'Reserved', 'Available', 'Monthly Budget', 'Usage', 'Alert Threshold', 'Last Updated', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((w, i) => {
                  const alloc   = Number(w.monthly_allocation || 0);
                  const balance = Number(w.balance || 0);
                  const reserved = Number(w.reserved || w.reserved_credits || 0);
                  const available = Number(w.available ?? (balance - reserved));
                  const used    = alloc - balance;
                  const pct     = alloc > 0 ? Math.min(100, (used / alloc * 100)) : 0;
                  const isAlert = pct >= Number(w.alert_threshold || 80);
                  return (
                    <tr key={w.tenant_id || i} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{w.tenant_name || w.tenant_id}</div>
                        <div className="text-xs text-gray-400 font-mono">{w.tenant_id}</div>
                        {w.partner_id && (
                          <span className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-full">
                            🤝 {w.partner_name || w.partner_id}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{fmt(balance, 2)}</td>
                      <td className="px-4 py-3 text-amber-600 font-medium">{fmt(reserved, 2)}</td>
                      <td className="px-4 py-3 text-green-600 font-medium">{fmt(available, 2)}</td>
                      <td className="px-4 py-3 text-gray-600">{alloc > 0 ? fmt(alloc, 0) : '—'}</td>
                      <td className="px-4 py-3 min-w-[140px]">
                        {alloc > 0 ? (
                          <div className="space-y-1">
                            <CreditBar used={used} total={alloc} reserved={reserved} />
                            <span className={`text-xs font-semibold ${isAlert ? 'text-red-600' : 'text-gray-500'}`}>
                              {pct.toFixed(1)}%
                            </span>
                          </div>
                        ) : <span className="text-gray-300 text-xs">No budget set</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isAlert ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
                          {w.alert_threshold || 80}%
                          {isAlert && ' 🔔'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{fmtDate(w.updated_at)}</td>
                      <td className="px-4 py-3">
                        {w.partner_id ? (
                          <button
                            onClick={() => setTab('pools')}
                            title="This tenant is funded via a Partner Pool. Switch to Partner Pools tab to top up."
                            className="flex items-center gap-1 text-xs font-semibold text-purple-600 hover:text-white border border-purple-200 hover:bg-purple-600 px-2.5 py-1.5 rounded-lg transition-all whitespace-nowrap">
                            <DynamicIcon name="Zap" className="w-3.5 h-3.5" /> Via Partner
                          </button>
                        ) : (
                          <button
                            onClick={() => setTopUpTenant(w)}
                            className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-white border border-indigo-200 hover:bg-indigo-600 px-2.5 py-1.5 rounded-lg transition-all whitespace-nowrap">
                            <DynamicIcon name="PlusCircle" className="w-3.5 h-3.5" /> Top Up
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Top-Up Modal */}
      {topUpTenant && (
        <TopUpModal
          tenant={topUpTenant}
          token={token}
          onClose={() => setTopUpTenant(null)}
          onSuccess={(amt, name) => {
            setTopUpTenant(null);
            setSuccessMsg(`Successfully added ${fmt(amt, 2)} credits to ${name}.`);
            load();
          }}
        />
      )}
      </>}
    </>
  );
}


// ════════════════════════════════════════════════════════════
// TENANT VIEW — Own Wallet Dashboard (existing)
// ════════════════════════════════════════════════════════════
function UsageTable({ rows, loading }) {
  const { formatDate: fmtDate } = useTimezoneFormatter();
  if (loading) return <div className="flex justify-center py-12"><LoadingSpinner /></div>;
  if (!rows?.length) return (
    <div className="text-center py-12 text-gray-400">
      <DynamicIcon name="Inbox" className="w-10 h-10 mx-auto mb-3 opacity-40" />
      <p>No usage records yet.</p>
    </div>
  );
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-100 text-sm">
        <thead className="bg-gray-50">
          <tr>
            {['Job ID','Module','Duration','Credits Used','Data (GB)','Status','Started'].map(h => (
              <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {rows.map((r, i) => (
            <tr key={r.id || i} className="hover:bg-gray-50 transition-colors">
              <td className="px-4 py-3 font-mono text-xs text-gray-400">{(r.job_id||'').substring(0,16)}…</td>
              <td className="px-4 py-3">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${MODULE_COLORS[r.module_type]||'bg-gray-100 text-gray-600'}`}>{r.module_type}</span>
              </td>
              <td className="px-4 py-3 text-gray-700">{fmt(r.duration_seconds,1)}s</td>
              <td className="px-4 py-3 font-semibold text-gray-900">{fmt(r.credits_consumed||r.credits_used,4)}</td>
              <td className="px-4 py-3 text-gray-500">{fmt(r.data_processed_gb,3)}</td>
              <td className="px-4 py-3">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[r.status]||'bg-gray-100 text-gray-500'}`}>{r.status}</span>
              </td>
              <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{fmtDate(r.started_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Transaction type metadata (icon, badge colour, credit direction) ──────────
const TXN_META = {
  topup:           { label: 'Top-Up',    icon: '⚡', color: 'bg-emerald-100 text-emerald-700', isGrant: true,  sign: '+' },
  plan_allocation: { label: 'Plan',      icon: '📦', color: 'bg-indigo-100 text-indigo-700',  isGrant: true,  sign: '+' },
  allocate:        { label: 'Partner',   icon: '🤝', color: 'bg-purple-100 text-purple-700',  isGrant: true,  sign: '+' },
  reset:           { label: 'Reset',     icon: '🔄', color: 'bg-sky-100 text-sky-700',        isGrant: true,  sign: '+' },
  release:         { label: 'Release',   icon: '↩',  color: 'bg-blue-100 text-blue-700',      isGrant: false, sign: '+' },
  deduction:       { label: 'Deduction', icon: '🔻', color: 'bg-red-100 text-red-700',        isGrant: false, sign: '−' },
  reserve:         { label: 'Reserve',   icon: '⏳', color: 'bg-amber-100 text-amber-700',    isGrant: false, sign: '−' },
  revoke:          { label: 'Revoke',    icon: '⛔', color: 'bg-rose-100 text-rose-700',      isGrant: false, sign: '−' },
};

function EnhancedTransactionView({ rows, loading, wallet }) {
  const { formatDate: fmtDate } = useTimezoneFormatter();
  if (loading) return <div className="flex justify-center py-12"><LoadingSpinner /></div>;
  if (!rows?.length) return (
    <div className="text-center py-16 text-gray-400">
      <DynamicIcon name="Receipt" className="w-10 h-10 mx-auto mb-3 opacity-30" />
      <p className="text-sm">No transactions yet.</p>
      <p className="text-xs mt-1">Transactions will appear here after credits are allocated or consumed.</p>
    </div>
  );

  // Split: grants (credits IN) vs operational (reserve, deduct, release)
  const grants = rows.filter(r => TXN_META[r.type || r.transaction_type]?.isGrant);
  const totalGranted = grants.reduce((s, r) => s + Number(r.amount || 0), 0);
  const balance   = Number(wallet?.balance || 0);
  const alloc     = Number(wallet?.monthly_allocation || 0);
  const reference = alloc > 0 ? alloc : totalGranted;
  const used      = reference > 0 ? Math.max(0, reference - balance) : 0;
  const usedPct   = reference > 0 ? Math.min(100, (used / reference) * 100) : 0;
  const barColor  = usedPct >= 90 ? 'bg-red-500' : usedPct >= 70 ? 'bg-amber-400' : 'bg-emerald-500';

  const fmtShortDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—';
  const txnId = (t, i) => {
    const raw = t.id || t.reference_id || '';
    return 'TXN-' + (raw ? raw.replace(/[-_]/g,'').substring(0,8).toUpperCase() : String(i+1).padStart(4,'0'));
  };

  return (
    <div>
      {/* ════ Section 1: Credit Grants ════════════════════════════════ */}
      {grants.length > 0 && (
        <div className="px-6 pt-5 pb-6 border-b border-gray-100">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold text-gray-800">Credit Grants</h4>
            <span className="text-xs font-medium text-gray-400">
              {fmt(used, 2)} / {fmt(reference, 2)} credits used
            </span>
          </div>

          {/* Progress bar */}
          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden mb-1">
            <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${usedPct}%` }} />
          </div>
          <div className="flex justify-between text-xs text-gray-400 mb-5">
            <span>{usedPct.toFixed(1)}% consumed</span>
            <span>{fmt(balance, 2)} remaining</span>
          </div>

          {/* Grants table */}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                {['Received', 'Source', 'Amount', 'State', 'Cycle Resets'].map(h => (
                  <th key={h} className="pb-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide pr-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grants.map((g, i) => {
                const meta = TXN_META[g.type] || { label: g.type, icon: '●', color: 'bg-gray-100 text-gray-600' };
                const isLatest = i === 0;
                return (
                  <tr key={g.id || i} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors">
                    <td className="py-3 pr-4 text-gray-700 text-sm whitespace-nowrap">{fmtShortDate(g.created_at)}</td>
                    <td className="py-3 pr-4">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full ${meta.color}`}>
                        {meta.icon} {meta.label}
                      </span>
                    </td>
                    <td className="py-3 pr-4 font-semibold text-gray-900 tabular-nums">{fmt(g.amount, 2)} credits</td>
                    <td className="py-3 pr-4">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full ${
                        isLatest ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${isLatest ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                        {isLatest ? 'Available' : 'Past'}
                      </span>
                    </td>
                    <td className="py-3 text-gray-400 text-xs">
                      {wallet?.cycle_reset_day ? `Day ${wallet.cycle_reset_day} monthly` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ════ Section 2: Full Transaction Ledger ═══════════════════════ */}
      <div className="px-6 pt-5 pb-4">
        <p className="text-xs text-gray-400 mb-4">Showing last {rows.length} transaction{rows.length !== 1 ? 's' : ''}</p>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              {['TXN ID', 'Type', 'Amount', 'Balance After', 'Note', 'Date'].map(h => (
                <th key={h} className="pb-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide pr-4 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((t, i) => {
              const type = t.type || t.transaction_type || '';
              const meta = TXN_META[type] || { label: type, icon: '●', color: 'bg-gray-100 text-gray-600', sign: '' };
              const isCredit = meta.sign === '+';
              return (
                <tr key={t.id || i} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors">
                  {/* TXN ID */}
                  <td className="py-3 pr-4">
                    <span className="font-mono text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded border border-gray-100">
                      {txnId(t, i)}
                    </span>
                  </td>
                  {/* Type badge */}
                  <td className="py-3 pr-4">
                    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-0.5 rounded-full ${meta.color}`}>
                      {meta.icon} {meta.label.toUpperCase()}
                    </span>
                  </td>
                  {/* Amount */}
                  <td className={`py-3 pr-4 font-bold tabular-nums ${isCredit ? 'text-emerald-600' : 'text-red-500'}`}>
                    {isCredit ? '+' : '−'}{fmt(t.amount, 4)}
                  </td>
                  {/* Balance after */}
                  <td className="py-3 pr-4 text-gray-700 tabular-nums text-sm">{fmt(t.balance_after, 4)}</td>
                  {/* Note */}
                  <td className="py-3 pr-4 text-gray-400 text-xs max-w-[180px] truncate" title={t.note || ''}>
                    {t.note || '—'}
                  </td>
                  {/* Date */}
                  <td className="py-3 text-gray-400 text-xs whitespace-nowrap">{fmtDate(t.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BreakdownView({ token }) {
  const [breakdown, setBreakdown] = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    setLoading(true);
    getUsageBreakdown(token)
      .then(d => { setBreakdown(d?.breakdown || []); setTotal(d?.total_credits || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="flex justify-center py-12"><LoadingSpinner /></div>;
  if (!breakdown.length) return (
    <div className="text-center py-16 text-gray-400">
      <DynamicIcon name="PieChart" className="w-10 h-10 mx-auto mb-3 opacity-30" />
      <p>No completed jobs yet. Run a flow to see your credit breakdown.</p>
    </div>
  );

  return (
    <div className="p-6 space-y-4">
{breakdown.map(b => (
        <div key={b.module_type} className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <span>{MODULE_ICONS[b.module_type] || '⚫'}</span>
              <span className="font-semibold text-gray-800">{b.module_type}</span>
              <span className="text-xs text-gray-400">{b.job_count} jobs · {b.total_duration_minutes} min</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold text-gray-500">{b.percentage}%</span>
              <span className="font-bold text-gray-900">{Number(b.total_credits).toFixed(4)} credits</span>
            </div>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${MODULE_BAR_COLORS[b.module_type] || 'bg-gray-400'}`}
              style={{ width: `${b.percentage}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function TenantCreditsView({ token }) {
  const { formatDate: fmtDate } = useTimezoneFormatter();
  const [wallet, setWallet]           = useState(null);
  const [usage, setUsage]             = useState([]);
  const [txns, setTxns]               = useState([]);
  const [tab, setTab]                 = useState('billing');
  const [walletLoading, setWalletLoading] = useState(true);
  const [usageLoading, setUsageLoading]   = useState(false);
  const [txnLoading, setTxnLoading]       = useState(false);
  const [exporting, setExporting]         = useState(false);
  const [error, setError]                 = useState(null);
  const [notEnterprise, setNotEnterprise] = useState(false);

  const loadWallet = useCallback(async () => {
    setWalletLoading(true); setError(null); setNotEnterprise(false);
    try { setWallet(await getMyWallet(token)); }
    catch (e) {
      const msg = (e.message||'').toLowerCase();
      if (msg.includes('enterprise') || msg.includes('403')) setNotEnterprise(true);
      else if (!msg.includes('not found') && !msg.includes('404')) setError(e.message);
      setWallet(null);
    } finally { setWalletLoading(false); }
  }, [token]);

  const loadUsage = useCallback(async () => {
    setUsageLoading(true);
    try { const d = await getMyUsage(token,1,50); setUsage(d?.records||(Array.isArray(d)?d:[])); }
    catch(e){ setError(e.message); } finally { setUsageLoading(false); }
  }, [token]);

  const loadTxns = useCallback(async () => {
    setTxnLoading(true);
    try { const d = await getMyTransactions(token,1,50); setTxns(d?.transactions||(Array.isArray(d)?d:[])); }
    catch(e){ setError(e.message); } finally { setTxnLoading(false); }
  }, [token]);

  useEffect(() => { loadWallet(); loadTxns(); }, [loadWallet, loadTxns]);
  useEffect(() => { if (tab === 'usage') loadUsage(); }, [tab, loadUsage]);

  // Derived wallet values
  const available  = wallet ? Math.max(0, Number(wallet.balance) - Number(wallet.reserved_credits||wallet.reserved||0)) : 0;
  const alloc      = wallet ? Number(wallet.monthly_allocation||0) : 0;
  const usedSoFar  = alloc - Number(wallet?.balance||0);
  const pUsed      = alloc > 0 ? Math.min(100,(usedSoFar/alloc)*100) : 0;
  const alertPct   = Number(wallet?.alert_threshold||80);
  const isAlerting = pUsed >= alertPct;
  const barColor   = pUsed >= 90 ? 'bg-red-500' : pUsed >= 70 ? 'bg-amber-400' : 'bg-emerald-500';

  // Split transactions
  const BILLING_TYPES = ['deduction','topup','plan_allocation','allocate','reset','revoke'];
  const GRANT_TYPES   = ['topup','plan_allocation','allocate','reset'];

  const billingTxns = txns.filter(t => BILLING_TYPES.includes(t.type || t.transaction_type));
  const grants      = txns.filter(t => GRANT_TYPES.includes(t.type || t.transaction_type));
  const totalGranted = grants.reduce((s, r) => s + Number(r.amount || 0), 0);
  const reference    = alloc > 0 ? alloc : totalGranted;
  const usedGrant    = reference > 0 ? Math.max(0, reference - Number(wallet?.balance || 0)) : 0;
  const grantPct     = reference > 0 ? Math.min(100, (usedGrant / reference) * 100) : 0;
  const grantBar     = grantPct >= 90 ? 'bg-red-500' : grantPct >= 70 ? 'bg-amber-400' : 'bg-emerald-500';

  const fmtShortDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—';
  const makeTxnId = (t, i) => {
    const raw = t.id || t.reference_id || '';
    return 'TXN-' + (raw ? raw.replace(/[-_]/g,'').substring(0,8).toUpperCase() : String(i+1).padStart(4,'0'));
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const url = await exportUsageCSV(token);
      const a = document.createElement('a');
      a.href = url; a.download = 'credit_usage.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError('CSV export failed: ' + e.message); }
    finally { setExporting(false); }
  };

  const NAV = [
    { id: 'billing', label: 'Billing', icon: 'CreditCard', desc: 'Balance & history' },
    { id: 'usage',   label: 'Usage',   icon: 'Activity',   desc: 'Job consumption'  },
  ];

  if (walletLoading) return (
    <div className="flex flex-col items-center justify-center py-24 text-gray-400">
      <LoadingSpinner /><p className="mt-3 text-sm">Loading credit information…</p>
    </div>
  );

  if (notEnterprise) return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
        <DynamicIcon name="CreditCard" className="w-8 h-8 text-indigo-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-800 mb-2">Credit Management Not Available</h3>
      <p className="text-sm text-gray-400 max-w-sm">
        Credit tracking requires an active plan with a credit wallet. Contact your administrator.
      </p>
    </div>
  );

  return (
    <>
      {error && (
        <div className="mb-4 flex items-center justify-between bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)} className="ml-4 text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* ── 2-column layout: Sidebar + Content ── */}
      <div className="flex gap-5 items-start">

        {/* ── Left Sidebar ── */}
        <aside className="w-48 flex-shrink-0 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden sticky top-4">
          <div className="p-2">
            {NAV.map(n => (
              <button key={n.id} onClick={() => setTab(n.id)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-all mb-0.5 group ${
                  tab === n.id ? 'bg-indigo-50' : 'hover:bg-gray-50'
                }`}>
                <DynamicIcon name={n.icon}
                  className={`w-4 h-4 flex-shrink-0 transition-colors ${
                    tab === n.id ? 'text-indigo-600' : 'text-gray-400 group-hover:text-gray-600'
                  }`} />
                <div>
                  <p className={`text-sm font-semibold ${tab === n.id ? 'text-indigo-700' : 'text-gray-700'}`}>{n.label}</p>
                  <p className="text-xs text-gray-400">{n.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* ── Right Content Panel ── */}
        <div className="flex-1 min-w-0 space-y-5">

          {/* ════════════════════════════════════
              BILLING TAB
              ════════════════════════════════════ */}
          {tab === 'billing' && (
            <>
              {/* Block 1: Credit Balance Card */}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                      {wallet?.plan_name || 'Current Plan'}
                    </p>
                    <h2 className="text-3xl font-bold text-gray-900">
                      {fmt(available, 2)} <span className="text-base font-normal text-gray-400">credits</span>
                    </h2>
                    <p className="text-sm text-gray-500 mt-0.5">Available to use</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400">Monthly budget</p>
                    <p className="text-lg font-bold text-gray-700">{fmt(alloc, 0)}</p>
                    {wallet?.cycle_reset_day && (
                      <p className="text-xs text-gray-400 mt-0.5">Resets day {wallet.cycle_reset_day}</p>
                    )}
                  </div>
                </div>
                {/* Usage progress bar */}
                {alloc > 0 && (
                  <>
                    <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden mb-1.5">
                      <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pUsed}%` }} />
                    </div>
                    <div className="flex justify-between text-xs text-gray-400">
                      <span className={isAlerting ? 'text-red-500 font-semibold' : ''}>
                        {isAlerting ? '🔔 ' : ''}{pUsed.toFixed(1)}% used this period
                      </span>
                      <span>{fmt(usedSoFar, 2)} of {fmt(alloc, 0)} consumed</span>
                    </div>
                  </>
                )}
                {/* Wallet detail row */}
                <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-4 text-xs">
                  {[
                    { label: 'Alert threshold', value: `${alertPct}%` },
                    { label: 'Overdraft', value: wallet?.overdraft_allowed ? 'Enabled' : 'Disabled' },
                    { label: 'Last updated', value: fmtShortDate(wallet?.updated_at) },
                  ].map(item => (
                    <div key={item.label}>
                      <p className="text-gray-400 mb-0.5">{item.label}</p>
                      <p className="font-semibold text-gray-700">{item.value || '—'}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Block 2: Credit Grants */}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">Credit Grants</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Credits received from platform, plan, or partner</p>
                  </div>
                  <button onClick={loadTxns} disabled={txnLoading}
                    className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50">
                    <DynamicIcon name="RefreshCw" className={`w-3.5 h-3.5 ${txnLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
                {txnLoading ? (
                  <div className="flex justify-center py-10"><LoadingSpinner /></div>
                ) : !grants.length ? (
                  <div className="text-center py-12 text-gray-400">
                    <DynamicIcon name="Gift" className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No credit grants yet.</p>
                  </div>
                ) : (
                  <div className="px-5 pt-4 pb-5">
                    {/* Progress bar */}
                    <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                      <span className="font-medium">Total received</span>
                      <span className="text-gray-400">{fmt(usedGrant,2)} / {fmt(reference,2)} credits used</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-1">
                      <div className={`h-full rounded-full transition-all duration-500 ${grantBar}`} style={{ width: `${grantPct}%` }} />
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 mb-5">
                      <span>{grantPct.toFixed(1)}% consumed</span>
                      <span>{fmt(Number(wallet?.balance||0),2)} remaining</span>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100">
                          {['Received','Source','Amount','State','Expires'].map(h => (
                            <th key={h} className="pb-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide pr-4">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {grants.map((g, i) => {
                          const meta = TXN_META[g.type] || { label: g.type, icon: '●', color: 'bg-gray-100 text-gray-600' };
                          const isLatest = i === 0;
                          return (
                            <tr key={g.id||i} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors">
                              <td className="py-3 pr-4 text-gray-700 text-sm whitespace-nowrap">{fmtShortDate(g.created_at)}</td>
                              <td className="py-3 pr-4">
                                <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${meta.color}`}>
                                  {meta.icon} {meta.label}
                                </span>
                              </td>
                              <td className="py-3 pr-4 font-semibold text-gray-900 tabular-nums">{fmt(g.amount,2)} credits</td>
                              <td className="py-3 pr-4">
                                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full ${
                                  isLatest ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                                }`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${isLatest ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                                  {isLatest ? 'Available' : 'Past'}
                                </span>
                              </td>
                              <td className="py-3 text-gray-400 text-xs">
                                {wallet?.cycle_reset_day ? `Day ${wallet.cycle_reset_day} monthly` : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Block 3: Billing History */}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">Billing History</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Charges and credit additions</p>
                  </div>
                  <button onClick={loadTxns} disabled={txnLoading}
                    className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50">
                    <DynamicIcon name="RefreshCw" className={`w-3.5 h-3.5 ${txnLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
                {txnLoading ? (
                  <div className="flex justify-center py-10"><LoadingSpinner /></div>
                ) : !billingTxns.length ? (
                  <div className="text-center py-12 text-gray-400">
                    <DynamicIcon name="Receipt" className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No billing history yet.</p>
                  </div>
                ) : (
                  <div className="px-5 py-4">
                    <p className="text-xs text-gray-400 mb-4">Showing last {billingTxns.length} entries</p>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100">
                          {['Invoice ID','Type','Amount','Balance After','Note','Date'].map(h => (
                            <th key={h} className="pb-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide pr-4 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {billingTxns.map((t, i) => {
                          const type = t.type || t.transaction_type || '';
                          const meta = TXN_META[type] || { label: type, icon: '●', color: 'bg-gray-100 text-gray-600', sign: '' };
                          const isCredit = meta.sign === '+';
                          return (
                            <tr key={t.id||i} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors">
                              <td className="py-3 pr-4">
                                <span className="font-mono text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded border border-gray-100">
                                   {makeTxnId(t,i)}
                                 </span>
                               </td>
                               <td className="py-3 pr-4">
                                 <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full ${meta.color}`}>
                                   {meta.icon} {meta.label}
                                 </span>
                               </td>
                               <td className="py-3 pr-4 font-semibold tabular-nums">
                                 <span className={isCredit ? "text-emerald-600" : "text-red-500"}>
                                   {isCredit ? "+" : ""}{fmt(t.amount ?? t.credits_used ?? 0, 2)}
                                 </span>
                               </td>
                               <td className="py-3 pr-4 font-semibold text-gray-900 tabular-nums">{fmt(t.balance_after ?? 0, 2)}</td>
                               <td className="py-3 pr-4 text-gray-500 text-xs max-w-[180px] truncate">{t.note || t.description || "—"}</td>
                               <td className="py-3 text-gray-400 text-xs">{fmtShortDate(t.created_at)}</td>
                             </tr>
                           );
                         })}
                       </tbody>
                     </table>
                   </div>
                 )}
               </div>
             </>
           )}

           {/* ════ USAGE TAB ════ */}
           {tab === 'usage' && (
             <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
               <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100">
                 <div>
                   <h3 className="text-sm font-semibold text-gray-800">Usage</h3>
                   <p className="text-xs text-gray-400 mt-0.5">Credit consumption per job execution</p>
                 </div>
                 <div className="flex items-center gap-3">
                   <button onClick={handleExport} disabled={exporting}
                     className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 hover:text-white border border-emerald-200 hover:bg-emerald-600 px-3 py-1.5 rounded-lg transition-all disabled:opacity-50">
                     <DynamicIcon name={exporting ? 'Loader2' : 'Download'} className={`w-3.5 h-3.5 ${exporting ? 'animate-spin' : ''}`} />
                     {exporting ? 'Exporting…' : 'Export CSV'}
                   </button>
                   <button onClick={loadUsage} disabled={usageLoading}
                     className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50">
                     <DynamicIcon name="RefreshCw" className={`w-3.5 h-3.5 ${usageLoading ? 'animate-spin' : ''}`} />
                     Refresh
                   </button>
                 </div>
               </div>
               <UsageTable rows={usage} loading={usageLoading} />
             </div>
           )}

         </div>{/* end right panel */}
       </div>{/* end sidebar layout */}
     </>
   );
}


// ════════════════════════════════════════════════════════════

// PARTNER VIEW — Pool summary + per-tenant distribution
// ════════════════════════════════════════════════════════════
function CreditActionModal({ title, tenantName, actionLabel, accentClass, onSubmit, onClose }) {
  const [amount, setAmount] = useState('');
  const [note, setNote]     = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);

  const handle = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setError('Amount must be > 0'); return; }
    setLoading(true); setError(null);
    try { await onSubmit(amt, note); }
    catch (err) { setError(err.message || 'Action failed'); setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="font-semibold text-gray-900 text-sm">{title}</h3>
            <p className="text-xs text-gray-400 truncate max-w-[220px]">{tenantName}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400">
            <DynamicIcon name="X" className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={handle} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Credits <span className="text-red-400">*</span></label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-semibold">₢</span>
              <input type="number" min="0.01" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
                placeholder="e.g. 5000" required
                className="w-full pl-7 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Note <span className="text-gray-300">(optional)</span></label>
            <input type="text" value={note} onChange={e => setNote(e.target.value)} maxLength={200}
              placeholder="e.g. Q2 allocation" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2.5">⚠️ {error}</div>}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">Cancel</button>
            <button type="submit" disabled={loading || !amount}
              className={`flex-1 px-4 py-2.5 text-sm font-semibold text-white ${accentClass} disabled:opacity-50 rounded-xl transition-colors flex items-center justify-center gap-2`}>
              {loading ? <><DynamicIcon name="Loader2" className="w-4 h-4 animate-spin" /> Processing…</> : actionLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PartnerCreditsView({ token, user }) {
  const partnerId = user?.partnerId;
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [modal, setModal]       = useState(null); // { type: 'allocate'|'revoke'|'topup', tenant }
  const isAdmin = ['r1', 'r2'].includes(user?.roleId);

  const load = useCallback(async () => {
    if (!partnerId) return;
    setLoading(true); setError(null);
    try { setData(await getPartnerCredits(token, partnerId)); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token, partnerId]);

  useEffect(() => { load(); }, [load]);

  const handleAllocate = async (amt, note) => {
    await partnerAllocateCredits(token, partnerId, modal.tenant.tenant_id, amt, note);
    setModal(null);
    setSuccessMsg(`Allocated ${fmt(amt,2)} credits to ${modal.tenant.tenant_name}.`);
    load();
  };

  const handleRevoke = async (amt, note) => {
    await partnerRevokeCredits(token, partnerId, modal.tenant.tenant_id, amt, note);
    setModal(null);
    setSuccessMsg(`Revoked ${fmt(amt,2)} credits from ${modal.tenant.tenant_name}.`);
    load();
  };

  const handleTopUp = async (amt, note) => {
    await partnerTopUpPool(token, partnerId, amt, note);
    setModal(null);
    setSuccessMsg(`Added ${fmt(amt,2)} credits to partner pool.`);
    load();
  };

  if (!partnerId) return (
    <div className="text-center py-24 text-gray-400">
      <DynamicIcon name="AlertTriangle" className="w-10 h-10 mx-auto mb-3 opacity-40" />
      <p>No partner associated with your account.</p>
    </div>
  );

  return (
    <>
      {/* Pool Summary Cards */}
      {data && (
        <div className="flex flex-wrap gap-4 mb-6">
          <StatCard label="Total Pool" value={fmt(data.pool_total,2)} sub="Given by Platform Admin" icon="Database" borderColor="border-purple-500" />
          <StatCard label="Allocated" value={fmt(data.pool_allocated,2)} sub="Distributed to tenants" icon="Share2" borderColor="border-indigo-500" />
          <StatCard label="Available" value={fmt(data.pool_available,2)} sub="Ready to distribute" icon="CheckCircle" borderColor="border-green-500" />
          <StatCard label="Tenants" value={data.tenant_count} sub="Under this partner" icon="Users" borderColor="border-blue-400" />
        </div>
      )}

      {data && (() => {
        const poolEmpty = data.pool_total > 0 && data.pool_available <= 0;
        const poolLow   = data.pool_total > 0 && !poolEmpty && (data.pool_available / data.pool_total) < 0.1;
        return (
          <>
            {poolEmpty && (
              <div className="mb-4 flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
                <DynamicIcon name="AlertOctagon" className="w-4 h-4 flex-shrink-0" />
                <span><strong>Pool Empty</strong> — No credits available to allocate. Ask your Platform Admin to top up your partner pool.</span>
              </div>
            )}
            {poolLow && (
              <div className="mb-4 flex items-center gap-3 bg-amber-50 border border-amber-200 text-amber-700 rounded-xl px-4 py-3 text-sm">
                <DynamicIcon name="AlertTriangle" className="w-4 h-4 flex-shrink-0" />
                <span><strong>Pool Running Low</strong> — Only {fmt(data.pool_available, 2)} credits remaining ({((data.pool_available / data.pool_total) * 100).toFixed(1)}%). Consider requesting a top-up.</span>
              </div>
            )}
          </>
        );
      })()}

      {successMsg && (
        <div className="mb-5 flex items-center justify-between bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm">
          <span>✅ {successMsg}</span>
          <button onClick={() => setSuccessMsg(null)} className="ml-4 text-green-400 hover:text-green-600">✕</button>
        </div>
      )}

      {/* Tenant Credit Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800 text-sm">My Tenants — Credit Balances</h3>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <button onClick={() => setModal({ type: 'topup', tenant: null })}
                className="flex items-center gap-1.5 text-xs font-semibold text-purple-600 hover:text-white border border-purple-200 hover:bg-purple-600 px-3 py-1.5 rounded-lg transition-all">
                <DynamicIcon name="PlusCircle" className="w-3.5 h-3.5" /> Top Up Pool
              </button>
            )}
            <button onClick={load} disabled={loading}
              className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50">
              <DynamicIcon name="RefreshCw" className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>

        {error && <div className="mx-5 my-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-2.5 text-sm">⚠️ {error}</div>}

        {loading ? (
          <div className="flex justify-center py-12"><LoadingSpinner /></div>
        ) : !data?.tenants?.length ? (
          <div className="text-center py-12 text-gray-400">
            <DynamicIcon name="Inbox" className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p>No tenants found for this partner.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-50 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Tenant', 'Plan', 'Current Balance', 'Available', 'Monthly Budget', 'Net Allocated', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.tenants.map((t, i) => {
                  const pct = t.monthly_allocation > 0
                    ? Math.min(100, ((t.monthly_allocation - t.current_balance) / t.monthly_allocation) * 100) : 0;
                  const isAlert = pct >= 80;
                  const poolEmpty = data.pool_total > 0 && data.pool_available <= 0;
                  return (
                    <tr key={t.tenant_id || i} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{t.tenant_name}</div>
                        <div className="text-xs text-gray-400 font-mono">{t.tenant_id}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{t.plan_id || '—'}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{fmt(t.current_balance, 2)}</td>
                      <td className="px-4 py-3">
                        <span className={`font-semibold ${t.available_credits < 100 ? 'text-red-500' : 'text-green-600'}`}>
                          {fmt(t.available_credits, 2)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden w-24">
                            <div className={`h-full rounded-full ${isAlert ? 'bg-red-500' : 'bg-indigo-500'}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs text-gray-400">{fmt(t.monthly_allocation, 0)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm font-semibold ${t.net_allocated_by_partner > 0 ? 'text-indigo-600' : 'text-gray-400'}`}>
                          {fmt(t.net_allocated_by_partner, 2)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => !poolEmpty && setModal({ type: 'allocate', tenant: t })}
                            disabled={poolEmpty}
                            title={poolEmpty ? 'Pool is empty — request a top-up from Platform Admin' : 'Allocate credits from your pool'}
                            className={`flex items-center gap-1 text-xs font-semibold border px-2 py-1 rounded-lg transition-all whitespace-nowrap ${
                              poolEmpty
                                ? 'text-gray-400 border-gray-200 cursor-not-allowed opacity-50'
                                : 'text-indigo-600 hover:text-white border-indigo-200 hover:bg-indigo-600'
                            }`}>
                            <DynamicIcon name="PlusCircle" className="w-3 h-3" /> Allocate
                          </button>
                          <button onClick={() => setModal({ type: 'revoke', tenant: t })}
                            className="flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-white border border-red-200 hover:bg-red-500 px-2 py-1 rounded-lg transition-all whitespace-nowrap">
                            <DynamicIcon name="MinusCircle" className="w-3 h-3" /> Revoke
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>


      {/* Allocate Modal */}
      {modal?.type === 'allocate' && (
        <CreditActionModal
          title="Allocate Credits" tenantName={modal.tenant.tenant_name}
          actionLabel={<><DynamicIcon name="PlusCircle" className="w-4 h-4" /> Allocate</>}
          accentClass="bg-indigo-600 hover:bg-indigo-700"
          onSubmit={handleAllocate} onClose={() => setModal(null)}
        />
      )}

      {/* Revoke Modal */}
      {modal?.type === 'revoke' && (
        <CreditActionModal
          title="Revoke Credits" tenantName={modal.tenant.tenant_name}
          actionLabel={<><DynamicIcon name="MinusCircle" className="w-4 h-4" /> Revoke</>}
          accentClass="bg-red-500 hover:bg-red-600"
          onSubmit={handleRevoke} onClose={() => setModal(null)}
        />
      )}

      {/* Pool Top-Up Modal (Platform Admin only) */}
      {modal?.type === 'topup' && (
        <CreditActionModal
          title="Top Up Partner Pool" tenantName={`Partner: ${partnerId}`}
          actionLabel={<><DynamicIcon name="Zap" className="w-4 h-4" /> Add to Pool</>}
          accentClass="bg-purple-600 hover:bg-purple-700"
          onSubmit={handleTopUp} onClose={() => setModal(null)}
        />
      )}
    </>
  );
}


// ════════════════════════════════════════════════════════════
// ROOT — Role-aware entry point
// ════════════════════════════════════════════════════════════
export default function CreditsPage({ isAdminView }) {
  const { user, token } = useAuth();

  const isPartnerAdmin  = user?.roleId === 'r3';
  const isPlatformAdmin = ['r1', 'r2'].includes(user?.roleId);
  const showAdmin = isAdminView !== undefined ? isAdminView : isPlatformAdmin;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader title="Credit Management" />

      <div className="p-6 max-w-7xl">
        {showAdmin
          ? <AdminCreditsView token={token} />
          : isPartnerAdmin
            ? <PartnerCreditsView token={token} user={user} />
            : <TenantCreditsView token={token} />
        }
      </div>
    </div>
  );
}
